import { useEffect, useRef, useState } from "react";

export function youtubeId(url) {
  if (!url) return "";
  const patterns = [
    /youtu\.be\/([\w-]{6,})/,
    /[?&]v=([\w-]{6,})/,
    /youtube\.com\/embed\/([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
    /youtube\.com\/live\/([\w-]{6,})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return /^[\w-]{6,}$/.test(url.trim()) ? url.trim() : "";
}

let apiPromise = null;
function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.onerror = () => {
      apiPromise = null;
      reject(new Error("YouTube IFrame API failed to load"));
    };
    document.head.appendChild(tag);
  });
  return apiPromise;
}

/**
 * Silent, chrome-free YouTube background that loops a trimmed [start, end] range.
 *
 * The IFrame API is required because the plain `end` parameter stops playback rather
 * than looping back to `start`.
 *
 * The player is mounted into a throwaway child node rather than onto our own ref:
 * YT.Player REPLACES the element it is given, so attaching directly to the ref left a
 * detached node behind whenever the effect re-ran (React StrictMode double-invokes
 * effects in development) and the second player silently rendered nothing.
 */
export default function YouTubeBackground({ videoId, start = 0, end = 0, onCycle, poster = "", posterStyle = {} }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!videoId || !hostRef.current) return undefined;

    let destroyed = false;
    const host = hostRef.current;
    host.replaceChildren();
    const mount = document.createElement("div");
    mount.className = "h-full w-full";
    host.appendChild(mount);

    setReady(false);
    setFailed(false);

    loadYouTubeApi()
      .then((YT) => {
        if (destroyed) return;
        playerRef.current = new YT.Player(mount, {
          videoId,
          host: "https://www.youtube-nocookie.com",
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            iv_load_policy: 3,
            origin: window.location.origin,
            start: start || 0,
            ...(end > start ? { end } : {}),
          },
          events: {
            onReady: (e) => {
              e.target.mute();
              e.target.playVideo();
              setReady(true);
            },
            onError: (e) => {
              // 101/150 = embedding disabled by the owner, 100 = removed, 2 = bad id
              // eslint-disable-next-line no-console
              console.warn(
                `[hero] YouTube video ${videoId} could not play (error ${e?.data}). Showing the poster image instead.`
              );
              setFailed(true);
            },
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.ENDED) {
                if (onCycle) onCycle();
                e.target.seekTo(start || 0, true);
                e.target.playVideo();
              }
            },
          },
        });
      })
      .catch(() => !destroyed && setFailed(true));

    return () => {
      destroyed = true;
      try {
        playerRef.current?.destroy();
      } catch {
        /* player already torn down */
      }
      playerRef.current = null;
      host.replaceChildren();
    };
  }, [videoId, start, end, onCycle]);

  if (failed) {
    return <img src={poster} alt="" style={posterStyle} data-testid="youtube-fallback-poster" className="h-full w-full object-cover" />;
  }

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="youtube-background">
      {poster ? (
        <img
          src={poster}
          alt=""
          style={posterStyle}
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${ready ? "opacity-0" : "opacity-100"}`}
        />
      ) : null}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-1000"
        style={{
          width: "max(100vw, 177.78vh)",
          height: "max(56.25vw, 100vh)",
          opacity: ready ? 1 : 0,
        }}
      >
        <div ref={hostRef} className="h-full w-full" />
      </div>
    </div>
  );
}
